import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FaSearch, FaUser, FaTimes, FaArrowLeft } from "react-icons/fa";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { AppDispatch } from "../../store";
import { fetchMessages } from "../../features/message/Messageservice";
import { useDebounce } from "../../util/hooks/useDebounce";
import { ChatEvent, phoneKey, subscribeChatEvents } from "../../features/live-chat/chatEvents";
import { whtsappIcon } from "../../asset";

interface Props {
    selectedPhone: string;
    onSelectContact: (phone: string) => void;
}

const PAGE_SIZE = 20;
// Max API calls per "load more" (the API returns messages, so one page can contain the same number many times; 
// we keep fetching until we have 50 new contacts or run out of pages)
const MAX_CALLS_PER_LOAD = 10;

interface Preview {
    text: string;
    at: Date;
}

// Latest live message as one line: text, caption or "Photo" / "Video"…
const previewText = (e: ChatEvent) => {
    const text = (e.caption || e.message || "").trim();
    const kind = String(e.messageType || "").toLowerCase();
    const label: Record<string, string> = {
        image: "📷 Photo",
        video: "🎥 Video",
        audio: "🎤 Audio",
        document: "📄 Document",
        sticker: "Sticker",
    };
    const body = label[kind] ? (text && !text.startsWith("[") ? `${label[kind]} · ${text}` : label[kind]) : text || "Message";
    return e.direction === "OUTBOUND" ? `You: ${body}` : body;
};

// Stable pastel avatar colour per phone number (like WhatsApp)
const AVATAR_COLORS = [
    "bg-[#dfe5e7] text-[#54656f]",
    "bg-[#d9fdd3] text-[#008069]",
    "bg-[#e0f2fe] text-[#0369a1]",
    "bg-[#fde2e4] text-[#be123c]",
    "bg-[#fef3c7] text-[#b45309]",
    "bg-[#ede9fe] text-[#6d28d9]",
];

export const avatarColor = (phone: string) => {
    let hash = 0;
    for (let i = 0; i < phone.length; i++) {
        hash = (hash * 31 + phone.charCodeAt(i)) >>> 0;
    }
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// "919876543210" → "+91 98765 43210" (falls back to the raw value)
export const formatPhone = (phone: string) => {
    const digits = phone.replace(/[^\d+]/g, "");
    if (!digits) return phone;
    const parsed = parsePhoneNumberFromString(digits.startsWith("+") ? digits : `+${digits}`);
    return parsed?.isValid() ? parsed.formatInternational() : phone;
};

export default function LiveChatContacts({
    selectedPhone,
    onSelectContact,
}: Props) {
    const dispatch = useDispatch<AppDispatch>();

    const [contacts, setContacts] = useState<string[]>([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchFocused, setSearchFocused] = useState(false);
    // Filled by live messages (keyed by phoneKey)
    const [unread, setUnread] = useState<Record<string, number>>({});
    const [previews, setPreviews] = useState<Record<string, Preview>>({});

    // Same server-side search as Message Logs (filters.toNumber)
    const debouncedSearch = useDebounce(search, 500);

    // Refs so the scroll observer always sees the latest values
    const nextPageRef = useRef(0);
    const seenRef = useRef<Set<string>>(new Set());
    const hasMoreRef = useRef(true);
    const loadingRef = useRef(false);
    const requestIdRef = useRef(0); // ignores responses from an old search

    const listRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const loadMore = useCallback(
        async (reset = false) => {
            if (!reset && (loadingRef.current || !hasMoreRef.current)) return;

            const requestId = ++requestIdRef.current;

            if (reset) {
                nextPageRef.current = 0;
                seenRef.current = new Set();
                hasMoreRef.current = true;
                setHasMore(true);
                setContacts([]);
                listRef.current?.scrollTo({ top: 0 });
            }

            loadingRef.current = true;
            setLoading(true);

            try {
                const fresh: string[] = [];
                let more = true;
                let calls = 0;

                while (fresh.length < PAGE_SIZE && more && calls < MAX_CALLS_PER_LOAD) {
                    const page = nextPageRef.current;

                    const result = await dispatch(
                        fetchMessages({
                            page,
                            size: PAGE_SIZE,
                            filters: {
                                toNumber: debouncedSearch.trim(),
                                templateName: "",
                                status: "",
                                deliveryStatus: "",
                            },
                        })
                    ).unwrap();

                    // A newer search started while this was loading — drop it
                    if (requestId !== requestIdRef.current) return;

                    const batch = result.results || [];

                    batch.forEach((item) => {
                        const phone = String(item.toNumber ?? "").trim();
                        const key = phoneKey(phone);
                        if (key && !seenRef.current.has(key)) {
                            seenRef.current.add(key);
                            fresh.push(phone);
                        }
                    });

                    nextPageRef.current = page + 1;
                    calls++;

                    // page.count = total pages (same as Message Logs)
                    const totalPages = result.page?.count;
                    more =
                        totalPages != null
                            ? page + 1 < totalPages
                            : batch.length === PAGE_SIZE;
                }

                hasMoreRef.current = more;
                setHasMore(more);
                setContacts((prev) => (reset ? fresh : [...prev, ...fresh]));
            } catch (error) {
                console.error("Failed to load contacts", error);
            } finally {
                if (requestId === requestIdRef.current) {
                    loadingRef.current = false;
                    setLoading(false);
                }
            }
        },
        [dispatch, debouncedSearch]
    );

    // First load + reload whenever the search changes
    useEffect(() => {
        loadMore(true);
    }, [loadMore]);

    /* ---------- Live: a new message moves its chat to the top ---------- */
    const selectedRef = useRef(selectedPhone);
    selectedRef.current = selectedPhone;
    const searchRef = useRef(debouncedSearch);
    searchRef.current = debouncedSearch;

    useEffect(
        () =>
            subscribeChatEvents({
                onMessage: (e) => {
                    if (!e.created) return; // ticks only matter inside the chat
                    const key = phoneKey(e.mobile);
                    if (!key) return;

                    setPreviews((p) => ({ ...p, [key]: { text: previewText(e), at: new Date() } }));
                    if (e.direction === "INBOUND" && phoneKey(selectedRef.current) !== key) {
                        setUnread((u) => ({ ...u, [key]: (u[key] || 0) + 1 }));
                    }

                    // While searching, only chats that match the search are listed
                    const q = searchRef.current.replace(/\D/g, "");
                    if (searchRef.current.trim() && !String(e.mobile).replace(/\D/g, "").includes(q)) return;

                    seenRef.current.add(key);
                    setContacts((prev) => {
                        const existing = prev.find((p) => phoneKey(p) === key) ?? String(e.mobile).trim();
                        return [existing, ...prev.filter((p) => p !== existing)];
                    });
                },
            }),
        []
    );

    // Opening a chat reads it
    useEffect(() => {
        const key = phoneKey(selectedPhone);
        if (!key) return;
        setUnread((u) => {
            if (!u[key]) return u;
            const { [key]: _read, ...rest } = u;
            return rest;
        });
    }, [selectedPhone]);

    // Auto-select the first chat on first load
    useEffect(() => {
        if (!selectedPhone && !debouncedSearch && contacts.length > 0) {
            onSelectContact(contacts[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contacts]);

    // Load next 50 when the user scrolls near the bottom.
    // Re-observing after each load also fills the list if it's still too
    // short to scroll.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore();
            },
            { root: listRef.current, rootMargin: "150px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [loadMore, contacts.length]);

    const initialLoading = loading && contacts.length === 0;

    return (
        <aside className="w-[300px] lg:w-[380px] flex-shrink-0 flex flex-col bg-white border-r border-[#e9edef]">
            {/* Header */}
            <div className="h-[64px] flex-shrink-0 flex items-center justify-between px-5">
                <div className="flex items-center gap-2.5">
                    <img src={whtsappIcon} alt="WhatsApp" className="w-7 h-7" />
                    <span className="text-[22px] font-bold tracking-tight text-[#111b21]">
                        Chats
                    </span>
                </div>

                {contacts.length > 0 && (
                    <span className="text-[12px] font-medium text-[#008069] bg-[#e7fce3] rounded-full px-2.5 py-0.5">
                        {contacts.length}
                        {hasMore ? "+" : ""}
                    </span>
                )}
            </div>

            {/* Search */}
            <div className="px-3 pb-2">
                <div
                    className={`relative flex items-center h-[40px] rounded-full transition-colors ${
                        searchFocused
                            ? "bg-white ring-2 ring-[#00a884]"
                            : "bg-[#f0f2f5] hover:bg-[#e9edef]"
                    }`}
                >
                    <span className="absolute left-4 flex items-center pointer-events-none">
                        {searchFocused ? (
                            <FaArrowLeft className="text-[13px] text-[#00a884]" />
                        ) : (
                            <FaSearch className="text-[13px] text-[#54656f]" />
                        )}
                    </span>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        type="text"
                        inputMode="tel"
                        placeholder="Search or start a new chat"
                        className="w-full h-full bg-transparent pl-12 pr-10 text-[14.5px] text-[#111b21] placeholder-[#667781] outline-none"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            aria-label="Clear search"
                            className="absolute right-2 w-7 h-7 rounded-full flex items-center justify-center text-[#54656f] hover:bg-black/5"
                        >
                            <FaTimes className="text-[12px]" />
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div ref={listRef} className="wa-scroll flex-1 overflow-y-auto px-2 pb-2">
                {initialLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 h-[72px]">
                            <div className="w-[49px] h-[49px] rounded-full bg-[#f0f2f5] animate-pulse" />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 w-2/3 rounded-full bg-[#f0f2f5] animate-pulse" />
                                <div className="h-2.5 w-1/3 rounded-full bg-[#f0f2f5] animate-pulse" />
                            </div>
                        </div>
                    ))
                ) : contacts.length === 0 && !loading ? (
                    <div className="flex flex-col items-center text-center py-14 px-6">
                        <div className="w-14 h-14 rounded-full bg-[#f0f2f5] flex items-center justify-center mb-3">
                            <FaSearch className="text-[18px] text-[#8696a0]" />
                        </div>
                        <p className="text-[14px] text-[#111b21]">
                            {debouncedSearch ? "No chats found" : "No conversations yet"}
                        </p>
                        {debouncedSearch && (
                            <p className="text-[12.5px] text-[#667781] mt-1">
                                Try a different number
                            </p>
                        )}
                    </div>
                ) : (
                    contacts.map((phone) => {
                        const active = selectedPhone === phone;
                        const key = phoneKey(phone);
                        const count = unread[key] || 0;
                        const preview = previews[key];

                        return (
                            <button
                                key={phone}
                                type="button"
                                onClick={() => onSelectContact(phone)}
                                className={`group w-full flex items-center gap-3 px-3 rounded-xl text-left transition-colors ${
                                    active ? "bg-[#f0f2f5]" : "hover:bg-[#f5f6f6]"
                                }`}
                            >
                                <div
                                    className={`w-[49px] h-[49px] rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor(phone)}`}
                                >
                                    <FaUser className="text-[20px] opacity-80" />
                                </div>

                                <div className="min-w-0 flex-1 h-[72px] flex flex-col justify-center">
                                    <div className="flex items-baseline gap-2">
                                        <p
                                            className={`min-w-0 flex-1 text-[16px] truncate ${
                                                active || count ? "font-medium text-[#111b21]" : "text-[#111b21]"
                                            }`}
                                        >
                                            {formatPhone(phone)}
                                        </p>
                                        {preview && (
                                            <span
                                                className={`flex-shrink-0 text-[12px] ${
                                                    count ? "font-medium text-[#00a884]" : "text-[#667781]"
                                                }`}
                                            >
                                                {preview.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {preview ? (
                                            <p
                                                className={`min-w-0 flex-1 text-[13px] truncate ${
                                                    count ? "text-[#111b21]" : "text-[#667781]"
                                                }`}
                                            >
                                                {preview.text}
                                            </p>
                                        ) : (
                                            <p className="min-w-0 flex-1 flex items-center gap-1 text-[13px] text-[#667781] truncate">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#25d366] flex-shrink-0" />
                                                WhatsApp
                                            </p>
                                        )}
                                        {count > 0 && (
                                            <span
                                                className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#25d366] text-white text-[11.5px] font-semibold flex items-center justify-center"
                                                aria-label={`${count} unread`}
                                            >
                                                {count > 99 ? "99+" : count}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}

                {/* Scroll trigger + footer */}
                <div ref={sentinelRef} className="h-12 flex items-center justify-center">
                    {loading && contacts.length > 0 && (
                        <span className="w-5 h-5 rounded-full border-2 border-[#00a884] border-t-transparent animate-spin" />
                    )}
                    {!loading && !hasMore && contacts.length > 0 && (
                        <span className="text-[12px] text-[#8696a0]">
                            {contacts.length} chats
                        </span>
                    )}
                </div>
            </div>
        </aside>
    );
}
